import { SERIES, toneColor, type SeriesTone } from "@/components/visual/chart-colors";
import { niceTicks, type BarDatum, type ColumnDatum, type ColumnReference, type HeatmapCell, type HeatmapColumn, type HeatmapRow, type LineSeries, type PhaseProgressDatum, type StackedColumnDatum } from "@/components/visual/charts";
import type { VisualTable, VisualTableRow } from "@/components/visual/visual-table";
import { addDays, toDay, weekdayOfDay, type ISODate } from "@/engine/date";
import { formatDate, weekdayNames } from "@/lib/date-format";
import { fmtCurrency, fmtDelta, fmtSpi } from "@/lib/format";
import { fmtMillion, type MonthBucket, type ProjectCostSummary, type YearMonth } from "@/features/resources/portfolio";
import { CHANGE_CATEGORIES, CHANGE_CATEGORY_LABEL, type ChangeCategory, type ChangeItem, type ReportContext, type ReportMetrics } from "./report-model";
import type { ReportTrendRow } from "./report-ui";

/*
 * 리포트 차트 어댑터(순수 함수). 입력은 시간순(오래된 → 최신) 행이다.
 * 화면은 여기서 만든 LineSeries/ColumnDatum/BarDatum/VisualTable을 그대로 차트·표에 넘긴다.
 */

export function burnPct(metrics: Pick<ReportMetrics, "burn">): number {
  return Math.round(metrics.burn * 100);
}

/** 가장 오래된 시점부터 선택한 시점까지. 과거 마감을 볼 때는 그 뒤 시점을 뺀다. rows는 최신 → 과거 순. */
export function historyUpTo(rows: readonly ReportTrendRow[], pointKey: string): ReportTrendRow[] {
  const index = rows.findIndex((row) => row.point.key === pointKey);
  return index < 0 ? [] : rows.slice(index).reverse();
}

/** 최신 → 과거 순 행을 시간순으로 */
export function chronological(rows: readonly ReportTrendRow[]): ReportTrendRow[] {
  return [...rows].reverse();
}

/* ---------------------------------------------------------------- 축 */

export interface TrendAxis {
  /** 눈금 글자: 작성 중인 현재는 「현재」, 마감은 기준일(09/05) */
  labels: string[];
  /** 콜아웃 머리: 기간 이름 · 기준일 */
  titles: string[];
  keys: string[];
}

export function trendAxis(rows: readonly ReportTrendRow[]): TrendAxis {
  return {
    labels: rows.map((r) => (r.point.kind === "current" ? "현재" : formatDate(r.m.statusDate, "short"))),
    titles: rows.map((r) => `${r.point.kind === "current" ? "현재 (작성 중)" : r.point.label} · 기준일 ${formatDate(r.m.statusDate, "numeric")}`),
    keys: rows.map((r) => r.point.key),
  };
}

/* ---------------------------------------------------------------- 기간 추이 시리즈 */

/** 진척: 실제(브랜드) + 계획(기준선, 점선). 기준선이 한 번도 없으면 계획 선은 뺀다. */
export function progressSeries(rows: readonly ReportTrendRow[]): LineSeries[] {
  const actual: LineSeries = { key: "actual", legend: "실제", color: SERIES.actual, values: rows.map((r) => r.m.progress), valueLabels: rows.map((r) => `${r.m.progress}%`) };
  const planned = rows.map((r) => r.m.plannedProgress);
  if (planned.every((v) => v === null)) return [actual];
  return [actual, { key: "plan", legend: "계획(기준선)", color: SERIES.plan, dashed: true, values: planned, valueLabels: planned.map((v) => (v === null ? undefined : `${v}%`)) }];
}

/** SPI: 실제 + 판단 기준선(1.0 계획대로, 0.9 위험). 기준선이 없으면 빈 배열(빈 상태로 안내). */
export function spiSeries(rows: readonly ReportTrendRow[]): LineSeries[] {
  const values = rows.map((r) => r.m.spi);
  if (values.every((v) => v === null)) return [];
  const flat = (y: number) => rows.map(() => y);
  return [
    { key: "spi", legend: "SPI", color: SERIES.actual, values, valueLabels: values.map((v) => (v === null ? undefined : fmtSpi(v))) },
    { key: "ref-1", legend: "계획대로 1.0", color: SERIES.reference, reference: true, values: flat(1) },
    { key: "ref-0.9", legend: "위험 0.9", color: SERIES.bad, reference: true, values: flat(0.9) },
  ];
}

/** 예산 대비 계획 비용(%): 금액이 가려져도 비율은 보인다. 예산이 없으면 빈 배열. */
export function burnSeries(rows: readonly ReportTrendRow[]): LineSeries[] {
  const values = rows.map((r) => (r.m.budget > 0 ? burnPct(r.m) : null));
  if (values.every((v) => v === null)) return [];
  return [
    { key: "burn", legend: "예산 대비", color: SERIES.actual, values, valueLabels: values.map((v) => (v === null ? undefined : `${v}%`)) },
    { key: "ref-100", legend: "예산 100%", color: SERIES.bad, reference: true, values: rows.map(() => 100) },
  ];
}

/** 소진율 y축 상한: 100% 선 위로 여유를 두고 25 단위로 올린다(눈금이 25·50 단위로 떨어지게). */
export function burnAxisMax(rows: readonly ReportTrendRow[]): number {
  const max = Math.max(100, ...rows.map((r) => (r.m.budget > 0 ? burnPct(r.m) : 0)));
  return Math.ceil((max + 5) / 25) * 25;
}

/** SPI y축: 0.5~1.2를 기본으로 두고 값이 벗어나면 넓힌다. */
export function spiAxis(rows: readonly ReportTrendRow[]): { min: number; max: number } {
  const vals = rows.flatMap((r) => (r.m.spi === null ? [] : [r.m.spi]));
  const min = Math.min(0.5, ...vals);
  const max = Math.max(1.2, ...vals);
  return { min: Math.floor(min * 10) / 10, max: Math.ceil(max * 10) / 10 };
}

function varianceTone(m: ReportMetrics): { tone: SeriesTone; legend: string } {
  if (!m.hasBaseline) return { tone: "empty", legend: "기준선 없음" };
  if (m.finishVarianceDays > 0) return { tone: "bad", legend: "기준선보다 늦음" };
  if (m.finishVarianceDays < 0) return { tone: "good", legend: "기준선보다 빠름" };
  return { tone: "empty", legend: "기준선과 같음" };
}

/** 기준선 대비 종료일 편차(근무일). 위로 갈수록 늦음. highlightKey가 있으면 나머지는 연한 톤으로 물린다. */
export function varianceColumns(rows: readonly ReportTrendRow[], options: { highlightKey?: string | null; onPick?: (key: string) => void } = {}): ColumnDatum[] {
  const axis = trendAxis(rows);
  return rows.map((r, i) => {
    const { tone, legend } = varianceTone(r.m);
    const dimmed = Boolean(options.highlightKey) && options.highlightKey !== r.point.key;
    return {
      key: r.point.key,
      label: axis.labels[i]!,
      calloutTitle: axis.labels[i],
      value: r.m.hasBaseline ? r.m.finishVarianceDays : 0,
      valueLabel: r.m.hasBaseline ? fmtDelta(r.m.finishVarianceDays) : "—",
      legend,
      color: toneColor(tone, dimmed),
      onClick: options.onPick ? () => options.onPick?.(r.point.key) : undefined,
    };
  });
}

/* ---------------------------------------------------------------- 표로 보기 */

export type TrendTableKind = "progress" | "variance" | "spi" | "burn" | "attention";

/** 기간별 막힘·초과·지연 누적 막대. */
export function attentionTrendStacks(
  rows: readonly ReportTrendRow[],
  options: { highlightKey?: string | null; onPick?: (key: string) => void } = {},
): StackedColumnDatum[] {
  const axis = trendAxis(rows);
  return rows.map((r, i) => {
    const dimmed = Boolean(options.highlightKey) && options.highlightKey !== r.point.key;
    return {
      key: r.point.key,
      label: axis.labels[i]!,
      calloutTitle: axis.titles[i],
      onClick: options.onPick ? () => options.onPick?.(r.point.key) : undefined,
      parts: [
        { key: "blocked", legend: "막힌 Task", value: r.m.execBlocked, color: toneColor("bad", dimmed), valueLabel: `${r.m.execBlocked}건` },
        { key: "overdue", legend: "마감 초과", value: r.m.execOverdue, color: toneColor("warn", dimmed), valueLabel: `${r.m.execOverdue}건` },
        { key: "delay", legend: "지연 작업(WBS)", value: r.m.delayCount, color: toneColor("actual", dimmed), valueLabel: `${r.m.delayCount}건` },
      ],
    };
  });
}

/** 각 추이 차트의 「표로 보기」. 차트와 같은 행 순서(시간순). */
export function trendTable(kind: TrendTableKind, rows: readonly ReportTrendRow[], onPick?: (key: string) => void): VisualTable {
  const base = (r: ReportTrendRow, cells: Array<string | number>): VisualTableRow => ({
    key: r.point.key,
    cells: [r.point.kind === "current" ? "현재 (작성 중)" : r.point.label, formatDate(r.m.statusDate, "numeric"), ...cells],
    onActivate: onPick ? () => onPick(r.point.key) : undefined,
    activateLabel: onPick ? "표에서 이 기간 보기" : undefined,
  });
  const head = [
    { key: "period", label: "보고 기간" },
    { key: "date", label: "진척 기준일", numeric: true },
  ];
  const gap = (m: ReportMetrics) => {
    if (m.plannedProgress === null) return "—";
    const d = m.progress - m.plannedProgress;
    return d === 0 ? "±0%p" : `${d > 0 ? "+" : ""}${d}%p`;
  };
  switch (kind) {
    case "progress":
      return {
        ariaLabel: "진척 추이 표",
        columns: [...head, { key: "actual", label: "실제", numeric: true }, { key: "plan", label: "계획", numeric: true }, { key: "gap", label: "차이", numeric: true }],
        rows: rows.map((r) => base(r, [`${r.m.progress}%`, r.m.plannedProgress === null ? "—" : `${r.m.plannedProgress}%`, gap(r.m)])),
      };
    case "variance":
      return {
        ariaLabel: "기준선 대비 종료일 편차 표",
        columns: [...head, { key: "finish", label: "예상 종료", numeric: true }, { key: "variance", label: "기준선 대비", numeric: true }],
        rows: rows.map((r) => base(r, [formatDate(r.m.projectFinish, "numeric"), r.m.hasBaseline ? fmtDelta(r.m.finishVarianceDays) : "—"])),
      };
    case "spi":
      return {
        ariaLabel: "SPI 추이 표",
        columns: [...head, { key: "spi", label: "SPI", numeric: true }],
        rows: rows.map((r) => base(r, [fmtSpi(r.m.spi)])),
      };
    case "burn":
      return {
        ariaLabel: "예산 대비 계획 비용 표",
        columns: [...head, { key: "burn", label: "예산 대비", numeric: true }],
        rows: rows.map((r) => base(r, [r.m.budget > 0 ? `${burnPct(r.m)}%` : "예산 미설정"])),
      };
    case "attention":
      return {
        ariaLabel: "병목 및 지연 추이 표",
        columns: [...head, { key: "blocked", label: "막힌 Task", numeric: true }, { key: "overdue", label: "마감 초과", numeric: true }, { key: "delay", label: "지연 작업", numeric: true }],
        rows: rows.map((r) => base(r, [`${r.m.execBlocked}건`, `${r.m.execOverdue}건`, `${r.m.delayCount}건`])),
      };
  }
}

/* ---------------------------------------------------------------- 직전 마감 대비 변경 */

export function changeCategoryBars(counts: Record<ChangeCategory, number>, options: { onPick?: (category: ChangeCategory) => void } = {}): BarDatum<ChangeCategory>[] {
  return CHANGE_CATEGORIES.map((category) => {
    const label = CHANGE_CATEGORY_LABEL[category];
    const value = counts[category];
    return {
      key: category,
      label,
      value,
      valueLabel: `${value}건`,
      color: value ? SERIES.actual : SERIES.empty,
      ariaLabel: `${label} ${value}건`,
      onClick: options.onPick && value ? () => options.onPick?.(category) : undefined,
    };
  });
}

/**
 * 막대는 구분별 건수다. 아래 두 열은 그 가운데 먼저 볼 변경과 나머지다.
 * 확인할 쪽은 위험·주의·삭제. 그 외는 나아진 것, 차이가 있는 것, 추가 순이다.
 */
export function changeSpotlights(items: readonly ChangeItem[], limit = 3): { attention: ChangeItem[]; rest: ChangeItem[]; attentionMore: number; restMore: number } {
  const attentionRank = (item: ChangeItem) => (item.tone === "danger" ? 0 : item.tone === "warning" ? 1 : 2);
  const restRank = (item: ChangeItem) => (item.tone === "success" ? 0 : item.delta !== undefined ? 1 : item.kind === "added" ? 2 : 3);
  const bySubject = (a: ChangeItem, b: ChangeItem) => a.subject.localeCompare(b.subject, "ko") || a.field.localeCompare(b.field, "ko") || a.id.localeCompare(b.id);
  const attentionRows = items.filter((item) => item.kind === "removed" || item.tone === "danger" || item.tone === "warning").sort((a, b) => attentionRank(a) - attentionRank(b) || bySubject(a, b));
  const restRows = items.filter((item) => item.kind !== "removed" && item.tone !== "danger" && item.tone !== "warning").sort((a, b) => restRank(a) - restRank(b) || bySubject(a, b));
  return {
    attention: attentionRows.slice(0, limit),
    rest: restRows.slice(0, limit),
    attentionMore: Math.max(0, attentionRows.length - limit),
    restMore: Math.max(0, restRows.length - limit),
  };
}

/* ---------------------------------------------------------------- 기준일까지 7일 */

/** 기준일 포함, 앞으로 센 하루 수. 「7일 마감」은 달력 주가 아니라 기준일에 끝나는 7일이다. */
const WEEK_SPAN = 7;
/** 마감 건수가 이 비율 안이면 같은 물량의 주로 본다. */
const SIMILAR_WEEK_RATIO = 0.2;
const SIMILAR_WEEK_MIN = 3;
/** 요일별 모양이 이 상관 이상일 때만 「분포가 비슷함」이라고 말한다. */
const ANALOG_CORRELATION = 0.8;

export interface WeekDueInput {
  statusDate: ISODate;
  tasks: readonly { due?: ISODate }[];
}

export interface WeekDuePeer extends WeekDueInput {
  key: string;
  /** 닮은 7일을 가리킬 때의 짧은 이름. 예: 「10/13」 */
  label: string;
}

export interface WeekDueChart {
  empty: boolean;
  dates: ISODate[];
  columns: ColumnDatum[];
  reference?: ColumnReference;
  yTicks: number[];
  table?: VisualTable;
  /** 직전 주 건수, 또는 분포가 닮은 주. 둘 다면 가운데 점으로 잇는다. */
  note?: string;
  ariaLabel: string;
}

function weekDates(statusDate: ISODate, earliest?: ISODate): ISODate[] {
  const start = addDays(statusDate, -(WEEK_SPAN - 1));
  const from = earliest && earliest > start ? earliest : start;
  const days: ISODate[] = [];
  for (let d = from; d <= statusDate; d = addDays(d, 1)) days.push(d);
  return days;
}

function duesOn(tasks: readonly { due?: ISODate }[], date: ISODate): number {
  let n = 0;
  for (const task of tasks) if (task.due === date) n += 1;
  return n;
}

function windowTotal(input: WeekDueInput): number {
  const days = weekDates(input.statusDate);
  const from = days[0]!;
  let n = 0;
  for (const task of input.tasks) if (task.due && task.due >= from && task.due <= input.statusDate) n += 1;
  return n;
}

/** 기준일에서 offset일 전(0 = 기준일, -6 = 일주일 전). */
function offsetVector(input: WeekDueInput): number[] {
  return Array.from({ length: WEEK_SPAN }, (_, i) => duesOn(input.tasks, addDays(input.statusDate, i - (WEEK_SPAN - 1))));
}

function pearson(a: readonly number[], b: readonly number[]): number | null {
  const n = a.length;
  if (n === 0 || n !== b.length) return null;
  const mean = (xs: readonly number[]) => xs.reduce((s, v) => s + v, 0) / n;
  const ma = mean(a);
  const mb = mean(b);
  let num = 0;
  let da = 0;
  let db = 0;
  for (let i = 0; i < n; i++) {
    const xa = a[i]! - ma;
    const xb = b[i]! - mb;
    num += xa * xb;
    da += xa * xa;
    db += xb * xb;
  }
  if (da < 1e-9 || db < 1e-9) return null;
  return num / Math.sqrt(da * db);
}

function countText(n: number): string {
  const rounded = Math.round(n * 10) / 10;
  return `${rounded}개`;
}

function volumeNote(current: number, previous: number): string {
  const delta = current - previous;
  if (delta === 0) return "지난 7일과 마감 건수가 같음";
  if (delta > 0) return `지난 7일보다 마감 ${delta}건 많음`;
  return `지난 7일보다 마감 ${-delta}건 적음`;
}

function emptyWeek(note?: string): WeekDueChart {
  return { empty: true, dates: [], columns: [], yTicks: [], note, ariaLabel: "" };
}

/**
 * 기준일에 끝나는 7일의 마감 분포.
 * 보이는 날은 근무일과 기준일, 그리고 마감이 있는 휴일. 점선은 하루 평균이고,
 * 물량이 비슷한 마감이 3구간 이상이면 직전 7일을 뺀 그 평균으로 바꾼다.
 */
export function weekDueChart(
  current: WeekDueInput,
  peers: readonly WeekDuePeer[],
  options: {
    isWorkingDay: (iso: ISODate) => boolean;
    /** 이 날보다 앞은 보고 기간 밖이라 막대에서 뺀다 */
    earliest?: ISODate;
    previousKey?: string | null;
    /** 고른 날. 이 7일 안에 없으면 기준일을 강조한다 */
    highlightDate?: ISODate | null;
    onPick?: (date: ISODate) => void;
  },
): WeekDueChart {
  const span = weekDates(current.statusDate);
  const counts = new Map(span.map((date) => [date, duesOn(current.tasks, date)]));
  const visible = weekDates(current.statusDate, options.earliest).filter((date) => date === current.statusDate || options.isWorkingDay(date) || (counts.get(date) ?? 0) > 0);
  const shown = visible.map((date) => ({ date, value: counts.get(date) ?? 0 }));
  const total = shown.reduce((sum, day) => sum + day.value, 0);

  const previous = options.previousKey ? peers.find((peer) => peer.key === options.previousKey) : undefined;
  const currentVector = offsetVector(current);
  const currentTotal = windowTotal(current);
  let analog: WeekDuePeer | null = null;
  let analogScore = 0;
  for (const peer of peers) {
    const score = pearson(currentVector, offsetVector(peer));
    if (score === null || score < ANALOG_CORRELATION) continue;
    if (!analog || score > analogScore + 1e-9 || (Math.abs(score - analogScore) <= 1e-9 && peer.statusDate > analog.statusDate)) {
      analog = peer;
      analogScore = score;
    }
  }
  const notes = [
    previous ? volumeNote(currentTotal, windowTotal(previous)) : undefined,
    analog && analog.key !== previous?.key ? `${analog.label} 마감과 분포가 비슷함` : undefined,
  ].filter((line): line is string => !!line);
  const note = notes.length ? notes.join(" · ") : undefined;
  if (total === 0) return emptyWeek(note);

  const similar =
    currentTotal > 0
      ? peers.filter((peer) => {
          if (peer.key === previous?.key) return false;
          const peerTotal = windowTotal(peer);
          const scale = Math.max(currentTotal, peerTotal);
          return Math.abs(currentTotal - peerTotal) / scale <= SIMILAR_WEEK_RATIO;
        })
      : [];
  const usePeers = similar.length >= SIMILAR_WEEK_MIN;
  const reference: ColumnReference | undefined =
    shown.length >= 2
      ? {
          legend: usePeers ? "비슷한 7일" : "평균",
          values: usePeers
            ? shown.map((day) => {
                const offset = toDay(day.date) - toDay(current.statusDate);
                const mean = similar.reduce((sum, peer) => sum + duesOn(peer.tasks, addDays(peer.statusDate, offset)), 0) / similar.length;
                return Math.round(mean * 10) / 10;
              })
            : shown.map(() => Math.round((total / shown.length) * 10) / 10),
        }
      : undefined;

  const requested = shown.some((day) => day.date === options.highlightDate) ? options.highlightDate! : null;
  // 기준일에 마감이 없으면 그 날을 강조하지 않는다. 0 높이 막대는 안 보여서, 실제 마감이 있는 날이 옅어지기만 한다.
  const focus = requested ?? ((counts.get(current.statusDate) ?? 0) > 0 ? current.statusDate : null);
  const columns: ColumnDatum[] = shown.map((day) => ({
    key: day.date,
    label: formatDate(day.date, "weekday"),
    calloutTitle: formatDate(day.date, "monthDayWeekday"),
    value: day.value,
    valueLabel: countText(day.value),
    legend: "마감",
    color: toneColor("actual", focus !== null && day.date !== focus),
    onClick: options.onPick ? () => options.onPick?.(day.date) : undefined,
  }));
  const peak = Math.max(...columns.map((column) => column.value), ...(reference?.values ?? [0]));
  const yTicks = niceTicks(Math.max(peak, 1), 4);
  const referenceHeader = reference ? [{ key: "reference", label: reference.legend, numeric: true }] : [];
  const table: VisualTable = {
    ariaLabel: "7일 마감 표",
    columns: [
      { key: "weekday", label: "요일" },
      { key: "date", label: "날짜", numeric: true },
      { key: "due", label: "마감", numeric: true },
      ...referenceHeader,
    ],
    rows: shown.map((day, index) => ({
      key: day.date,
      cells: [formatDate(day.date, "weekday"), formatDate(day.date, "numeric"), countText(day.value), ...(reference ? [countText(reference.values[index] ?? 0)] : [])],
      onActivate: options.onPick ? () => options.onPick?.(day.date) : undefined,
      activateLabel: options.onPick ? "이 날 마감 보기" : undefined,
    })),
  };
  return {
    empty: false,
    dates: shown.map((day) => day.date),
    columns,
    reference,
    yTicks,
    table,
    note,
    ariaLabel: `7일 마감: ${shown.map((day) => `${formatDate(day.date, "short")} ${formatDate(day.date, "weekday")} ${day.value}개`).join(", ")}`,
  };
}

/* ---------------------------------------------------------------- 최근 4주 × 요일 */

const MATRIX_WEEKS = 4;
/** 월요일부터. weekdayOfDay는 0이 일요일이다. */
const MATRIX_WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const;

function mondayOnOrBefore(iso: ISODate): ISODate {
  const weekday = weekdayOfDay(toDay(iso));
  return addDays(iso, weekday === 0 ? -6 : 1 - weekday);
}

export interface WeekDueMatrix {
  columns: HeatmapColumn[];
  rows: HeatmapRow[];
  /** columns 인덱스. 마감이 없으면 null */
  peakColumn: number | null;
  headline: string;
  empty: boolean;
  table: VisualTable;
  ariaLabel: string;
}

/**
 * 기준일이 속한 주부터 최근 4주. 열은 월–일.
 * 기준일 다음 날과 보고 기간 앞은 빈칸(null)이고, 그 안의 0건은 관측된 0이다.
 */
export function weekDueMatrix(
  input: WeekDueInput,
  options: { earliest?: ISODate; onPick?: (date: ISODate) => void } = {},
): WeekDueMatrix {
  const endMonday = mondayOnOrBefore(input.statusDate);
  const mondays = Array.from({ length: MATRIX_WEEKS }, (_, index) => addDays(endMonday, -7 * (MATRIX_WEEKS - 1 - index)));
  const short = weekdayNames("short");
  const long = weekdayNames("long");
  const columns: HeatmapColumn[] = MATRIX_WEEKDAYS.map((weekday) => ({ key: String(weekday), label: short[weekday] ?? "" }));
  const counts = new Map<string, number>();
  for (const task of input.tasks) {
    if (!task.due) continue;
    counts.set(task.due, (counts.get(task.due) ?? 0) + 1);
  }
  const sums = columns.map(() => 0);
  let known = 0;
  const rows: HeatmapRow[] = mondays.map((monday) => {
    const cells: HeatmapCell[] = MATRIX_WEEKDAYS.map((_, column) => {
      const date = addDays(monday, column);
      const outside = date > input.statusDate || (options.earliest !== undefined && date < options.earliest);
      const value = outside ? null : (counts.get(date) ?? 0);
      if (value !== null) {
        sums[column] = (sums[column] ?? 0) + value;
        known += value;
      }
      const when = formatDate(date, "monthDayWeekday");
      return {
        key: date,
        value,
        label: value === null ? undefined : String(value),
        tooltip: value === null ? `${when} · 기간 밖` : `${when} · ${value}건`,
        marked: date === input.statusDate,
        onClick: value !== null && options.onPick ? () => options.onPick?.(date) : undefined,
      };
    });
    return { key: monday, label: formatDate(monday, "short"), cells };
  });
  let peakColumn = -1;
  let peakCount = 0;
  sums.forEach((count, index) => {
    if (count > peakCount) {
      peakCount = count;
      peakColumn = index;
    }
  });
  if (known === 0) peakColumn = -1;
  const peakWeekday = peakColumn < 0 ? null : (MATRIX_WEEKDAYS[peakColumn] ?? null);
  const peakName = peakWeekday === null ? "" : (long[peakWeekday] ?? "");
  const headline = peakName ? `${peakName}에 마감이 가장 많습니다` : "";
  const spoken = rows.flatMap((row) => row.cells.flatMap((cell) => (cell.value === null ? [] : [cell.tooltip])));
  const table: VisualTable = {
    ariaLabel: "주별 요일 마감 표",
    columns: [{ key: "week", label: "주" }, ...columns.map((column) => ({ key: column.key, label: column.label, numeric: true }))],
    rows: rows.map((row) => {
      const best = row.cells.reduce<HeatmapCell | null>((pick, cell) => (cell.value !== null && cell.value > (pick?.value ?? -1) ? cell : pick), null);
      return {
        key: row.key,
        cells: [formatDate(row.key, "numeric"), ...row.cells.map((cell) => (cell.value === null ? "—" : `${cell.value}건`))],
        onActivate: options.onPick && best && (best.value ?? 0) > 0 ? () => options.onPick?.(best.key) : undefined,
        activateLabel: options.onPick ? "이 주의 마감이 많은 날 보기" : undefined,
      };
    }),
  };
  return {
    columns,
    rows,
    peakColumn: peakColumn < 0 ? null : peakColumn,
    headline,
    empty: known === 0,
    table,
    ariaLabel: headline ? `${headline}. ${spoken.join(", ")}` : "최근 4주에 마감이 없습니다",
  };
}

export function changeTable(counts: Record<ChangeCategory, number>, onPick?: (category: ChangeCategory) => void): VisualTable {
  return {
    ariaLabel: "직전 마감 대비 변경 표",
    columns: [
      { key: "category", label: "구분" },
      { key: "count", label: "변경 건수", numeric: true },
    ],
    rows: CHANGE_CATEGORIES.map((category) => ({
      key: category,
      cells: [CHANGE_CATEGORY_LABEL[category], `${counts[category]}건`],
      onActivate: onPick && counts[category] ? () => onPick(category) : undefined,
      activateLabel: onPick ? "변경 내역 보기" : undefined,
    })),
  };
}

export interface AssigneeDueRow {
  /** 담당 없음은 `none`. 작업 목록의 `?assignee=` 는 빈 문자열이다 */
  key: string;
  name: string;
  count: number;
}

/** 보고 기간 마감 중 끝나지 않은 Task를 담당별로 센다. 담당이 둘이면 양쪽에 넣는다. */
export function periodAssigneeCounts(tasks: readonly { status?: string; assignees: readonly string[] }[]): AssigneeDueRow[] {
  const map = new Map<string, number>();
  for (const task of tasks) {
    if (task.status === "done") continue;
    const names = task.assignees.length ? task.assignees : [""];
    for (const name of names) map.set(name, (map.get(name) ?? 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ko"))
    .map(([name, count]) => ({ key: name || "none", name: name || "담당 없음", count }));
}

/* ---------------------------------------------------------------- WBS 단계별 계획 대비 진척 */

export function phaseProgressBars(
  ctx: Pick<ReportContext, "project" | "schedule" | "baseline" | "calendar">,
  options: { onPick?: (taskId: string) => void } = {},
): PhaseProgressDatum[] {
  const byId = new Map(ctx.project.tasks.map((t) => [t.id, t]));
  const statusOrd = ctx.calendar.ordinalBeforeISO(ctx.project.statusDate) + 1;
  const topPhaseOf = (id: string) => {
    let cur = byId.get(id);
    const seen = new Set<string>();
    while (cur?.parentId && byId.has(cur.parentId) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.parentId);
    }
    return cur ?? null;
  };

  const phases: Array<{ id: string; wbs: string; name: string }> = [];
  const seen = new Set<string>();
  for (const id of ctx.schedule.order) {
    const p = topPhaseOf(id);
    if (!p || seen.has(p.id)) continue;
    seen.add(p.id);
    const st = ctx.schedule.tasks.get(p.id);
    phases.push({ id: p.id, wbs: st?.wbs ?? "", name: p.name || "이름 없는 단계" });
  }

  const acc = new Map(phases.map((p) => [p.id, { w: 0, ev: 0, bw: 0, pv: 0 }]));
  for (const id of ctx.schedule.order) {
    const s = ctx.schedule.tasks.get(id);
    if (!s || s.isSummary) continue;
    const p = topPhaseOf(id);
    if (!p) continue;
    const bucket = acc.get(p.id);
    if (!bucket) continue;
    const w = Math.max(s.duration, 0.5);
    bucket.w += w;
    bucket.ev += w * s.percentComplete;
    const b = ctx.baseline?.tasks[id];
    if (b) {
      const bw = Math.max(b.duration, 0.5);
      const startOrd = ctx.calendar.ordinalOfISO(b.start);
      const endOrd = b.duration > 0 ? startOrd + b.duration : startOrd + 1;
      const plannedRatio = Math.max(0, Math.min(1, (statusOrd - startOrd) / (endOrd - startOrd)));
      bucket.bw += bw;
      bucket.pv += bw * plannedRatio * 100;
    }
  }

  return phases.map((p) => {
    const b = acc.get(p.id)!;
    const actual = b.w > 0 ? Math.round(b.ev / b.w) : 0;
    const planned = ctx.baseline && b.bw > 0 ? Math.round(b.pv / b.bw) : null;
    const gap = planned !== null ? actual - planned : 0;
    const color = actual >= 100 ? SERIES.good : gap <= -15 ? SERIES.bad : gap <= -5 ? SERIES.warn : SERIES.actual;
    return {
      key: p.id,
      wbs: p.wbs,
      label: p.name,
      actual,
      planned,
      color,
      onClick: options.onPick ? () => options.onPick?.(p.id) : undefined,
    };
  });
}

/** 단계 진척의 분포. 계획 아래 개수와, 가장 높은 진척·가장 낮은 진척. */
export function phaseDistributionLine(rows: readonly Pick<PhaseProgressDatum, "actual" | "planned">[]): string | null {
  if (rows.length === 0) return null;
  const count = rows.length;
  const actuals = rows.map((row) => row.actual);
  const high = Math.max(...actuals);
  const low = Math.min(...actuals);
  const judged = rows.filter((row) => row.planned !== null);
  const behind = judged.filter((row) => row.planned !== null && row.actual < row.planned).length;
  const spreadText = count < 2 ? null : high === low ? `진척 ${high}%` : `앞 ${high}% · 뒤 ${low}%`;

  if (judged.length === 0) return spreadText ? `${count}단계 · ${spreadText}` : `${count}단계`;
  if (count === 1) {
    const row = rows[0]!;
    if (row.planned === null) return "1단계";
    const gap = row.actual - row.planned;
    if (gap === 0) return "1단계 · 계획과 같음";
    if (gap < 0) return `1단계 · 계획보다 ${-gap}%p 낮음`;
    return `1단계 · 계획보다 ${gap}%p 앞섬`;
  }
  const head = judged.length === count && behind === 0 ? `${count}단계 모두 계획 이상` : `${count}단계 중 계획 아래 ${behind}개`;
  return spreadText ? `${head} · ${spreadText}` : head;
}

export interface PhaseMover {
  key: string;
  wbs: string;
  label: string;
  actual: number;
  /** 직전 마감 대비 실제 진척 차이(%p). */
  delta: number;
}

export function formatPhasePoints(delta: number): string {
  if (delta === 0) return "±0%p";
  return `${delta > 0 ? "+" : ""}${delta}%p`;
}

/**
 * 직전 마감과 같은 단계만 견준다. 오른 쪽은 진척이 늘어난 단계, 늘지 않은 쪽은 그대로이거나 줄어든 미완료 단계.
 * 이미 100%인 단계는 늘지 않은 쪽에 넣지 않는다.
 */
export function phaseMovers(
  current: readonly Pick<PhaseProgressDatum, "key" | "wbs" | "label" | "actual" | "planned">[],
  previous: readonly Pick<PhaseProgressDatum, "key" | "actual">[],
  limit = 3,
): { gains: PhaseMover[]; stalls: PhaseMover[]; gainMore: number; stallMore: number } {
  const prior = new Map(previous.map((row) => [row.key, row.actual]));
  const compared = current.flatMap((row) => {
    const before = prior.get(row.key);
    if (before === undefined) return [];
    return [{ key: row.key, wbs: row.wbs, label: row.label, actual: row.actual, planned: row.planned, delta: row.actual - before }];
  });
  const byName = (a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label, "ko");
  const gainRows = compared.filter((row) => row.delta > 0).sort((a, b) => b.delta - a.delta || byName(a, b));
  const stallRows = compared.filter((row) => row.delta <= 0 && row.actual < 100).sort((a, b) => a.delta - b.delta || gapOf(a) - gapOf(b) || byName(a, b));
  return {
    gains: gainRows.slice(0, limit).map(toMover),
    stalls: stallRows.slice(0, limit).map(toMover),
    gainMore: Math.max(0, gainRows.length - limit),
    stallMore: Math.max(0, stallRows.length - limit),
  };
}

function gapOf(row: { actual: number; planned: number | null }): number {
  return row.planned === null ? 0 : row.actual - row.planned;
}

function toMover(row: { key: string; wbs: string; label: string; actual: number; delta: number }): PhaseMover {
  return { key: row.key, wbs: row.wbs, label: row.label, actual: row.actual, delta: row.delta };
}

/* ---------------------------------------------------------------- 계획 비용 */

export type CostPartKey = "labor" | "overhead" | "fee" | "solution" | "expense";

export interface CostPart {
  key: CostPartKey;
  label: string;
  amount: number;
}

/** 예산 화면의 비용 구조와 같은 순서·이름. 0원인 항목은 막대에서 뺀다. */
export function costParts(summary: Pick<ProjectCostSummary, "directLabor" | "overhead" | "technicalFee" | "solution" | "expense">): CostPart[] {
  return [
    { key: "labor", label: "직접인건비", amount: summary.directLabor },
    { key: "overhead", label: "제경비", amount: summary.overhead },
    { key: "fee", label: "기술료", amount: summary.technicalFee },
    { key: "solution", label: "솔루션·라이선스", amount: summary.solution },
    { key: "expense", label: "직접경비", amount: summary.expense },
  ];
}

/** 막대 길이는 합계 대비 비중(%). 금액은 콜아웃·표에 둔다. */
export function costCompositionBars(parts: readonly CostPart[], options: { masked?: boolean; onPick?: (key: CostPartKey) => void } = {}): BarDatum<CostPartKey>[] {
  const visible = parts.filter((p) => p.amount > 0);
  const total = visible.reduce((sum, p) => sum + p.amount, 0);
  return visible.map((p) => {
    const pct = total > 0 ? Math.round((p.amount / total) * 100) : 0;
    const money = fmtCurrency(Math.round(p.amount), options.masked);
    const text = options.masked ? `${pct}%` : `${pct}% · ${money}`;
    return {
      key: p.key,
      label: p.label,
      value: pct,
      valueLabel: text,
      color: SERIES.actual,
      ariaLabel: `${p.label} ${text}`,
      onClick: options.onPick ? () => options.onPick?.(p.key) : undefined,
    };
  });
}

export function costCompositionTable(parts: readonly CostPart[], options: { masked?: boolean; onPick?: (key: CostPartKey) => void } = {}): VisualTable {
  const bars = costCompositionBars(parts, options);
  return {
    ariaLabel: "비용 구성 표",
    columns: [
      { key: "part", label: "구분" },
      { key: "share", label: "비중", numeric: true },
      { key: "amount", label: "금액", numeric: true },
    ],
    rows: bars.map((bar) => {
      const amount = parts.find((p) => p.key === bar.key)?.amount ?? 0;
      return {
        key: bar.key,
        cells: [bar.label, `${bar.value}%`, fmtCurrency(Math.round(amount), options.masked)],
        onActivate: bar.onClick,
        activateLabel: bar.onClick ? "예산에서 보기" : undefined,
      };
    }),
  };
}

export interface CostMonthModel {
  columns: ColumnDatum[];
  reference?: ColumnReference;
  yTicks: number[];
  /** won: 축은 백만 원. share: 금액이 가려져 합계 대비 비중. */
  axis: "won" | "share";
  table: VisualTable;
  /** 진척 기준일이 기간 안에 있을 때. 예) 2026년 10월 */
  focusLabel?: string;
  /** 막대가 없고 예산 기준선도 없을 때 */
  empty: boolean;
}

const WON = 1_000_000;

function million(amount: number): number {
  return Math.round((amount / WON) * 10) / 10;
}

/** 월별 축 눈금. won은 백만 원(32M), share는 퍼센트. */
export function formatCostAxis(axis: CostMonthModel["axis"], value: number): string {
  if (axis === "share") return `${Math.round(value)}%`;
  if (value <= 0) return "0";
  return fmtMillion(value * WON);
}

/**
 * 월별 계획 비용. 값은 백만 원 단위라 축이 읽힌다.
 * 기준월만 진하게 두고, 예산이 있고 금액이 보이면 월 균등 점선을 얹는다.
 * 금액이 가려지면 축은 비중이고 예산선은 빼 금액이 눈금으로 새지 않게 한다.
 */
export function costMonthModel(
  buckets: readonly MonthBucket[],
  options: { budget: number; statusMonth: YearMonth; masked?: boolean; onPick?: (ym: YearMonth) => void },
): CostMonthModel {
  const masked = options.masked === true;
  const sum = buckets.reduce((s, b) => s + b.total, 0);
  const hasFocus = buckets.some((b) => b.ym === options.statusMonth);
  const even = options.budget > 0 && buckets.length > 0 ? options.budget / buckets.length : 0;
  const showReference = !masked && even > 0;
  const axis: CostMonthModel["axis"] = masked ? "share" : "won";

  const columns: ColumnDatum[] = buckets.map((b) => {
    const focus = hasFocus && b.ym === options.statusMonth;
    const dimmed = hasFocus && !focus;
    const share = sum > 0 ? Math.round((b.total / sum) * 1000) / 10 : 0;
    const money = fmtCurrency(Math.round(b.total), masked);
    return {
      key: b.ym,
      label: formatDate(b.ym, "yearMonthShort"),
      calloutTitle: formatDate(b.ym, "yearMonth"),
      value: masked ? share : million(b.total),
      valueLabel: masked ? `${share}%` : money,
      legend: focus ? "기준월" : dimmed ? "다른 달" : "계획 비용",
      color: toneColor("actual", dimmed),
      onClick: options.onPick ? () => options.onPick?.(b.ym) : undefined,
    };
  });

  const reference: ColumnReference | undefined = showReference
    ? { legend: "월 균등", values: buckets.map(() => million(even)), valueLabel: fmtCurrency(Math.round(even)) }
    : undefined;
  const peak = Math.max(0, ...columns.map((c) => c.value), ...(reference?.values ?? []));
  const yTicks = niceTicks(Math.max(1, Math.ceil(peak)), 4);
  const focusLabel = hasFocus ? formatDate(options.statusMonth, "yearMonth") : undefined;
  const table: VisualTable = {
    ariaLabel: "월별 계획 비용 표",
    columns: [
      { key: "month", label: "월" },
      { key: "total", label: "계획 비용", numeric: true },
      ...(showReference ? [{ key: "even", label: "월 균등", numeric: true }] : []),
    ],
    rows: buckets.map((b) => ({
      key: b.ym,
      cells: [formatDate(b.ym, "yearMonth"), fmtCurrency(Math.round(b.total), masked), ...(showReference ? [fmtCurrency(Math.round(even))] : [])],
      onActivate: options.onPick ? () => options.onPick?.(b.ym) : undefined,
      activateLabel: options.onPick ? "예산에서 월별 보기" : undefined,
    })),
  };

  return {
    columns,
    reference,
    yTicks,
    axis,
    table,
    focusLabel,
    empty: buckets.length === 0 || (buckets.every((b) => b.total <= 0) && !showReference),
  };
}

export function phaseProgressTable(rows: readonly PhaseProgressDatum[]): VisualTable {
  const hasPlan = rows.some((r) => r.planned !== null);
  return {
    ariaLabel: "WBS 단계별 진척 표",
    columns: [
      { key: "wbs", label: "WBS" },
      { key: "phase", label: "단계" },
      { key: "actual", label: "실제 진척", numeric: true },
      ...(hasPlan
        ? [
            { key: "planned", label: "계획 진척", numeric: true },
            { key: "gap", label: "차이", numeric: true },
          ]
        : []),
    ],
    rows: rows.map((r) => {
      const gap = r.planned === null ? "—" : r.actual - r.planned === 0 ? "±0%p" : `${r.actual - r.planned > 0 ? "+" : ""}${r.actual - r.planned}%p`;
      return {
        key: r.key,
        cells: [r.wbs, r.label, `${r.actual}%`, ...(hasPlan ? [r.planned === null ? "—" : `${r.planned}%`, gap] : [])],
        onActivate: r.onClick,
        activateLabel: r.onClick ? `${r.label} 일정에서 보기` : undefined,
      };
    }),
  };
}

